import type { RootState } from 'client/js/redux';
import * as React from 'react';
import { connect } from 'react-redux';
import type { AccessControlReducer } from '../../../main/types/accessControlTypes';
import { hasAccess } from '../../support/utils/accessControlHelpers';

type InjectedProps = {
  accessControl: AccessControlReducer;
};

type ExternalProps = {
  accessResources: Array<string>;
  children: React.ReactElement | React.FC<Record<string, boolean | undefined>>;
  permission: number;

  /**
   * name of the prop that will be passed to children
   * It will be set to true if access denied
   */
  accessDeniedPropName?: string;
};

type Props = InjectedProps & ExternalProps;

/**
 * Wrapper component which checks if the user has required access
 * and passes in a prop to the children
 */
export class AccessControlWrapper extends React.Component<Props> {
  render() {
    const accessDeniedPropName = this.props.accessDeniedPropName || 'disabled';

    if (hasAccess(this.props.accessResources, this.props.permission, this.props.accessControl.data)) {
      if (typeof this.props.children === 'function') {
        return this.props.children({ [accessDeniedPropName]: undefined });
      }
      return React.Children.only(this.props.children);
    }

    const childrenProps = {
      [accessDeniedPropName]: true,
    };
    if (typeof this.props.children === 'function') {
      return this.props.children(childrenProps);
    }
    return React.cloneElement(this.props.children, childrenProps);
  }
}

const mapStateToProps = (state: RootState) => ({
  accessControl: state.accessControl,
});

const ConnectedAccessControlWrapper: React.ComponentType<ExternalProps> = connect(mapStateToProps)(AccessControlWrapper);

export default ConnectedAccessControlWrapper;
